/**
 * ImportFilePanel — AK-368c «Импорт полей из файла на карте», AK-369 Ф1-UI.
 *
 * Монтируется в v2.tsx под styleReady когда importStore.phase !== 'idle' (файл
 * загружен). Рисует всё превью-состояние импорта поверх карты /map:
 *   - parsing → лоадер «Читаем файл…» по центру карты (фрейм 2);
 *   - preview → правая панель-список: назначение (хозяйство + отделение +
 *     режим имён), счётчик, строки (чекбокс, инлайн-имя, га, статус-иконка,
 *     дедуп-бейджи), сворачиваемая группа «Дубликаты (K)», «Добавить N полей»
 *     (фреймы 3-5);
 *   - saving → прогресс (фрейм 5);
 *   - error  → центрированная карточка (unreadable/too-large/empty/…) (фрейм 6).
 * Плюс top-center тосты: успех с «Отменить импорт» (откат пачки, фрейм 6).
 *
 * AK-369 дельта к AK-368:
 *   1. Дедуп — бэк отдаёт preview.features[].duplicates; строки-дубликаты
 *      собраны в сворачиваемую группу «Дубликаты (K)», по умолчанию сняты,
 *      с бейджем «дубликат: <имя> (N%)» и массовым «исключить все».
 *   2. Имена — 3 режима (Авто / На карте / Таблицей) вместо бинарного файл/авто.
 *      «На карте»: клик контура → инлайн-инпут у контура (v2.tsx onNameOnMap).
 *      «Таблицей»: компактная таблица №/имя/га/статус, Tab по колонке имён.
 *   3. Отделение/бригада — дропдаун + инлайн «＋ Создать отделение».
 *   4. Откат — тост после save с «Отменить импорт» (onUndoImport).
 *
 * Геометрию на карте рисует ImportController (v2.tsx по подписке). Панель НЕ
 * держит карту — только данные importStore.
 *
 * DS: только @/components/ui/* + Lucide + Earth Tech токены. Ноль hex вне
 * whitelist. Адаптив: список в overflow, без 100vw/vh.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Loader2,
  X,
  Check,
  Pencil,
  Trash2,
  FileWarning,
  FileText,
  Ban,
  ChevronDown,
  ChevronRight,
  Plus,
  Undo2,
  TriangleAlert,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  useImportStore,
  selectedCount,
  validCount,
  isDuplicate,
  topDuplicate,
  type ImportRow,
} from '@/lib/map/import/importStore';
import { nextFieldName } from '@/lib/map/addField/autoName';

interface Farm {
  id: string;
  name: string;
}

interface Brigade {
  id: string;
  name: string;
}

interface ImportFilePanelProps {
  /** Хозяйства для дропдауна назначения. */
  farms: Farm[];
  /** Отделения/бригады выбранного хозяйства (для дропдауна назначения). */
  brigades: Brigade[];
  /** Существующие имена полей (для автоген «Поле N» при nameMode='auto'). */
  existingNames: string[];
  /** Клик по строке — подсветить контур на карте. */
  onRowFocus: (index: number | null) => void;
  /** «Уточнить контур» строки — v2.tsx откроет редактор terra-draw. */
  onEditContour: (index: number) => void;
  /** «Добавить N полей» — v2.tsx выполнит save + MVT-инвалидацию. */
  onSave: () => void;
  /** Инлайн-создание отделения (имя) → id нового отделения (или null при ошибке). */
  onCreateBrigade: (name: string) => Promise<string | null>;
  /** «Отменить импорт» из тоста — откат пачки batchId (архивирование). */
  onUndoImport: (batchId: string) => void;
}

/** Форматирует площадь в «N,N га» (1 знак ≥10 га, иначе 2). */
function formatAreaHa(ha: number | null): string {
  if (ha == null || !Number.isFinite(ha) || ha <= 0) return '—';
  const digits = ha >= 10 ? 1 : 2;
  return `${ha.toFixed(digits).replace('.', ',')} га`;
}

export function ImportFilePanel({
  farms,
  brigades,
  existingNames,
  onRowFocus,
  onEditContour,
  onSave,
  onCreateBrigade,
  onUndoImport,
}: ImportFilePanelProps) {
  const phase = useImportStore((s) => s.phase);
  const fileName = useImportStore((s) => s.fileName);
  const rows = useImportStore((s) => s.rows);
  const warnings = useImportStore((s) => s.warnings);
  const fileError = useImportStore((s) => s.fileError);
  const farmId = useImportStore((s) => s.farmId);
  const brigadeId = useImportStore((s) => s.brigadeId);
  const nameMode = useImportStore((s) => s.nameMode);
  const dupGroupCollapsed = useImportStore((s) => s.dupGroupCollapsed);
  const editingIndex = useImportStore((s) => s.editingIndex);
  const saveProgress = useImportStore((s) => s.saveProgress);
  const successToast = useImportStore((s) => s.successToast);
  const lastBatchId = useImportStore((s) => s.lastBatchId);
  const errorToast = useImportStore((s) => s.errorToast);
  const toggleSelected = useImportStore((s) => s.toggleSelected);
  const setAllSelected = useImportStore((s) => s.setAllSelected);
  const renameRow = useImportStore((s) => s.renameRow);
  const removeRow = useImportStore((s) => s.removeRow);
  const excludeAllDuplicates = useImportStore((s) => s.excludeAllDuplicates);
  const toggleDupGroup = useImportStore((s) => s.toggleDupGroup);
  const setFarmId = useImportStore((s) => s.setFarmId);
  const setBrigadeId = useImportStore((s) => s.setBrigadeId);
  const setNameMode = useImportStore((s) => s.setNameMode);
  const reset = useImportStore((s) => s.reset);
  const clearSuccessToast = useImportStore((s) => s.clearSuccessToast);
  const clearErrorToast = useImportStore((s) => s.clearErrorToast);

  // Разделяем строки: обычные vs дубликаты (AK-369 сворачиваемая группа).
  const dupRows = useMemo(() => rows.filter(isDuplicate), [rows]);
  const normalRows = useMemo(() => rows.filter((r) => !isDuplicate(r)), [rows]);

  const sel = selectedCount(rows);
  const valid = validCount(rows);
  const total = rows.length;

  // Автодизмисс тостов. Success живёт дольше (7с) — успеть нажать «Отменить».
  useEffect(() => {
    if (!successToast) return;
    const t = window.setTimeout(clearSuccessToast, 7000);
    return () => window.clearTimeout(t);
  }, [successToast, clearSuccessToast]);
  useEffect(() => {
    if (!errorToast) return;
    const t = window.setTimeout(clearErrorToast, 5000);
    return () => window.clearTimeout(t);
  }, [errorToast, clearErrorToast]);

  // Автоген имён «Поле N» при nameMode='auto' — стартовый номер от existingNames.
  const autoBase = useMemo(() => nextFieldName(existingNames), [existingNames]);

  return (
    <>
      {/* ── Top-center тосты ─────────────────────────────────────────── */}
      {successToast && (
        <div
          data-testid="import-success-toast"
          className="absolute top-4 left-1/2 -translate-x-1/2 z-50 max-w-md rounded-xl bg-primary text-primary-foreground text-xs px-4 py-3 shadow-lg flex items-center gap-3"
        >
          <Check size={16} className="flex-none" />
          <span className="font-semibold">{successToast}</span>
          {lastBatchId && (
            <button
              type="button"
              data-testid="import-undo"
              onClick={() => onUndoImport(lastBatchId)}
              className="flex-none inline-flex items-center gap-1 rounded-md bg-primary-foreground/15 px-2 py-1 font-semibold hover:bg-primary-foreground/25"
            >
              <Undo2 size={14} />
              Отменить импорт
            </button>
          )}
        </div>
      )}
      {errorToast && (
        <div
          data-testid="import-error-toast"
          className="absolute top-4 left-1/2 -translate-x-1/2 z-50 max-w-md rounded-full bg-foreground text-background text-xs font-medium px-4 py-2 shadow-lg flex items-center gap-2"
        >
          <span>{errorToast}</span>
          <button
            type="button"
            aria-label="Закрыть"
            onClick={clearErrorToast}
            className="opacity-70 hover:opacity-100"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* ── Parsing loader (фрейм 2) ──────────────────────────────────── */}
      {phase === 'parsing' && (
        <div
          data-testid="import-parsing"
          className="absolute inset-0 z-40 flex items-center justify-center bg-foreground/40 backdrop-blur-[2px]"
        >
          <div className="w-[280px] rounded-xl bg-background p-6 text-center shadow-2xl">
            <Loader2 size={34} className="animate-spin text-accent-brand mx-auto mb-3" />
            <div className="text-sm font-bold text-foreground">Читаем файл…</div>
            <div className="text-xs text-muted-foreground mt-1">
              Разбираем контуры и считаем площадь
            </div>
            {fileName && (
              <div className="mt-3 inline-block rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                <FileText size={14} className="inline mr-1 -mt-1" />
                {fileName}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Error card (фрейм 6) ──────────────────────────────────────── */}
      {phase === 'error' && fileError && (
        <div
          data-testid="import-error-card"
          className="absolute inset-0 z-40 flex items-center justify-center p-6"
        >
          <div className="w-full max-w-sm rounded-xl border border-border bg-background p-6 text-center shadow-2xl">
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-xl bg-destructive/10 text-destructive">
              {fileError.kind === 'empty' ? <Ban size={24} /> : <FileWarning size={24} />}
            </div>
            <div className="text-base font-bold text-foreground mb-2">
              {errorTitle(fileError.kind)}
            </div>
            <p className="text-sm text-muted-foreground mb-4">{fileError.message}</p>
            {fileError.kind === 'empty' && rows.length > 0 && (
              <div className="mb-4 max-h-40 overflow-auto text-left rounded-md border border-border bg-muted/30 p-2">
                {rows.slice(0, 30).map((r) => (
                  <div key={r.index} className="text-xs text-muted-foreground py-1">
                    · {r.name || `Контур ${r.index}`}
                    {r.error ? ` — ${r.error}` : ''}
                  </div>
                ))}
              </div>
            )}
            <Button
              variant="default"
              size="sm"
              className="w-full"
              data-testid="import-error-retry"
              onClick={() => reset()}
            >
              Выбрать другой файл
            </Button>
          </div>
        </div>
      )}

      {/* ── Preview / Saving side panel (фреймы 3-5) ──────────────────── */}
      {(phase === 'preview' || phase === 'saving') && (
        <div
          data-testid="import-panel"
          // Непрозрачная поверхность (bg-popover), НЕ полупрозрачность над картой —
          // иначе спутник просвечивает и текст/контролы нечитаемы (AK-382 founder UAT).
          className="absolute right-0 top-0 bottom-0 z-40 flex w-[min(380px,calc(100%-16px))] flex-col border-l border-border bg-popover text-popover-foreground shadow-2xl"
        >
          {/* Шапка */}
          <div className="flex items-start justify-between border-b border-border px-4 py-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-sm font-bold text-foreground">
                <FileText size={16} className="text-accent-brand flex-none" />
                <span className="truncate">
                  {phase === 'saving' ? 'Сохраняем поля…' : 'Контуры из файла'}
                </span>
              </div>
              <div className="text-xs text-muted-foreground mt-1 truncate">
                {fileName ? `${fileName} · ` : ''}
                {total} {plural(total, 'контур', 'контура', 'контуров')}
                {dupRows.length > 0 ? (
                  <>
                    {' · '}
                    <b className="text-accent-brand">
                      {dupRows.length} {plural(dupRows.length, 'дубликат', 'дубликата', 'дубликатов')}
                    </b>
                  </>
                ) : (
                  ' найдено'
                )}
              </div>
            </div>
            {phase === 'preview' && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 -mt-1 -mr-1 flex-none"
                aria-label="Отменить импорт"
                data-testid="import-close"
                onClick={() => reset()}
              >
                <X size={16} />
              </Button>
            )}
          </div>

          {phase === 'saving' ? (
            <SavingBody progress={saveProgress} sel={sel} />
          ) : (
            <>
              {/* Назначение: хозяйство + отделение + режим имён */}
              <div className="flex flex-col gap-3 border-b border-border bg-muted/30 px-4 py-3">
                <div className="space-y-2">
                  <Label htmlFor="import-farm" className="text-xs uppercase tracking-wide">
                    Хозяйство · обязательно · одно на пачку
                  </Label>
                  <Select value={farmId ?? undefined} onValueChange={(v) => setFarmId(v)}>
                    <SelectTrigger id="import-farm" data-testid="import-farm">
                      <SelectValue placeholder="Выберите хозяйство" />
                    </SelectTrigger>
                    <SelectContent>
                      {farms.map((f) => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Отделение/бригада — только если выбрано хозяйство. */}
                {farmId && (
                  <BrigadeField
                    brigades={brigades}
                    brigadeId={brigadeId}
                    onSelect={setBrigadeId}
                    onCreate={onCreateBrigade}
                  />
                )}

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide">Имена полей</Label>
                  <div className="flex rounded-md border border-border overflow-hidden">
                    <SegBtn
                      active={nameMode === 'auto'}
                      onClick={() => setNameMode('auto')}
                      testId="import-names-auto"
                    >
                      Авто «Поле N»
                    </SegBtn>
                    <SegBtn
                      active={nameMode === 'map'}
                      onClick={() => setNameMode('map')}
                      testId="import-names-map"
                    >
                      На карте
                    </SegBtn>
                    <SegBtn
                      active={nameMode === 'table'}
                      onClick={() => setNameMode('table')}
                      testId="import-names-table"
                    >
                      Таблицей
                    </SegBtn>
                  </div>
                  {nameMode === 'map' && (
                    <div className="text-xs text-muted-foreground">
                      Кликните контур на карте — инпут имени откроется прямо у него.
                    </div>
                  )}
                </div>
              </div>

              {/* Warnings от бэка (усечение >500 и т.п.) */}
              {warnings.length > 0 && (
                <div className="border-b border-border bg-accent-brand/10 px-4 py-2 text-xs text-foreground/80">
                  {warnings.map((w, i) => (
                    <div key={i} className="flex gap-2">
                      <span aria-hidden>ⓘ</span>
                      <span>{w}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Счётчик + «снять/выбрать всё» */}
              <div className="flex items-center justify-between px-4 py-2 text-xs text-muted-foreground">
                <span>
                  Выбрано <b className="text-foreground">{sel}</b> из {valid}
                  {total > valid ? ` · ${total - valid} невалид.` : ''}
                </span>
                <button
                  type="button"
                  data-testid="import-toggle-all"
                  className="font-semibold text-primary hover:underline"
                  onClick={() => setAllSelected(sel < valid)}
                >
                  {sel < valid ? 'Выбрать всё' : 'Снять всё'}
                </button>
              </div>

              {/* Список / таблица контуров */}
              <ScrollArea className="flex-1 min-h-0">
                {nameMode === 'table' ? (
                  <NameTable
                    rows={rows}
                    autoBase={autoBase}
                    onRename={(index, name) => renameRow(index, name)}
                    onFocus={onRowFocus}
                  />
                ) : (
                  <div className="flex flex-col gap-1 px-2 pb-2">
                    {normalRows.map((r) => (
                      <ImportRowItem
                        key={r.index}
                        row={r}
                        dimmed={editingIndex !== null && editingIndex !== r.index}
                        isEditing={editingIndex === r.index}
                        autoName={
                          nameMode === 'auto'
                            ? autoNameForRow(autoBase, rows, r.index)
                            : null
                        }
                        onFocus={() => onRowFocus(r.index)}
                        onToggle={() => toggleSelected(r.index)}
                        onRename={(name) => renameRow(r.index, name)}
                        onEdit={() => onEditContour(r.index)}
                        onRemove={() => removeRow(r.index)}
                      />
                    ))}

                    {/* Сворачиваемая группа «Дубликаты (K)» (AK-369). */}
                    {dupRows.length > 0 && (
                      <div className="mt-1">
                        <div className="flex items-center gap-2 px-2 py-2">
                          <button
                            type="button"
                            data-testid="import-dup-toggle"
                            onClick={toggleDupGroup}
                            className="flex items-center gap-1 text-xs font-bold text-accent-brand hover:underline"
                          >
                            {dupGroupCollapsed ? (
                              <ChevronRight size={14} />
                            ) : (
                              <ChevronDown size={14} />
                            )}
                            Дубликаты ({dupRows.length})
                          </button>
                          <button
                            type="button"
                            data-testid="import-dup-exclude-all"
                            onClick={excludeAllDuplicates}
                            className="ml-auto text-xs font-semibold text-destructive hover:underline"
                          >
                            Исключить все
                          </button>
                        </div>
                        {!dupGroupCollapsed &&
                          dupRows.map((r) => (
                            <ImportRowItem
                              key={r.index}
                              row={r}
                              dimmed={editingIndex !== null && editingIndex !== r.index}
                              isEditing={editingIndex === r.index}
                              autoName={
                                nameMode === 'auto'
                                  ? autoNameForRow(autoBase, rows, r.index)
                                  : null
                              }
                              onFocus={() => onRowFocus(r.index)}
                              onToggle={() => toggleSelected(r.index)}
                              onRename={(name) => renameRow(r.index, name)}
                              onEdit={() => onEditContour(r.index)}
                              onRemove={() => removeRow(r.index)}
                            />
                          ))}
                      </div>
                    )}
                  </div>
                )}
              </ScrollArea>

              {/* Футер: сохранить */}
              <div className="border-t border-border px-4 py-3 flex flex-col gap-2">
                <Button
                  variant="default"
                  className="w-full"
                  data-testid="import-save"
                  disabled={!farmId || sel === 0 || editingIndex !== null}
                  onClick={onSave}
                >
                  {editingIndex !== null
                    ? 'Завершите правку контура'
                    : `Добавить ${sel} ${plural(sel, 'поле', 'поля', 'полей')}${
                        farmId ? ` в «${farms.find((f) => f.id === farmId)?.name ?? ''}»` : ''
                      }`}
                </Button>
                {!farmId && (
                  <div className="text-xs text-muted-foreground text-center">
                    Кнопка активна после выбора хозяйства
                  </div>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full"
                  data-testid="import-cancel"
                  onClick={() => reset()}
                >
                  Отмена
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

// ── подкомпоненты ────────────────────────────────────────────────────────

/**
 * Отделение/бригада: дропдаун + инлайн «＋ Создать отделение» (AK-369 фрейм 5).
 * Пусто → сразу показываем кнопку создания. Опциональное поле (можно «Не выбрано»).
 */
function BrigadeField({
  brigades,
  brigadeId,
  onSelect,
  onCreate,
}: {
  brigades: Brigade[];
  brigadeId: string | null;
  onSelect: (id: string | null) => void;
  onCreate: (name: string) => Promise<string | null>;
}) {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (creating) inputRef.current?.focus();
  }, [creating]);

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    const id = await onCreate(trimmed);
    setBusy(false);
    if (id) {
      onSelect(id);
      setName('');
      setCreating(false);
    }
  };

  const NONE = '__none__';

  return (
    <div className="space-y-2">
      <Label htmlFor="import-brigade" className="text-xs uppercase tracking-wide">
        Отделение / бригада · опционально
      </Label>
      {creating ? (
        <div className="flex items-center gap-2">
          <Input
            ref={inputRef}
            data-testid="import-brigade-new-name"
            value={name}
            placeholder="Название отделения"
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void submit();
              if (e.key === 'Escape') setCreating(false);
            }}
            className="h-8 text-sm"
          />
          <Button
            size="sm"
            className="h-8 px-3"
            data-testid="import-brigade-new-save"
            disabled={!name.trim() || busy}
            onClick={() => void submit()}
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label="Отмена"
            onClick={() => {
              setCreating(false);
              setName('');
            }}
          >
            <X size={14} />
          </Button>
        </div>
      ) : brigades.length === 0 ? (
        <Button
          variant="outline"
          size="sm"
          className="w-full justify-start h-8 text-sm font-normal"
          data-testid="import-brigade-create"
          onClick={() => setCreating(true)}
        >
          <Plus size={14} />
          Создать отделение
        </Button>
      ) : (
        <div className="flex items-center gap-2">
          <Select
            value={brigadeId ?? NONE}
            onValueChange={(v) => onSelect(v === NONE ? null : v)}
          >
            <SelectTrigger id="import-brigade" data-testid="import-brigade" className="flex-1">
              <SelectValue placeholder="Не выбрано" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Не выбрано</SelectItem>
              {brigades.map((b) => (
                <SelectItem key={b.id} value={b.id}>
                  {b.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 flex-none text-accent-brand"
            aria-label="Создать отделение"
            data-testid="import-brigade-create"
            onClick={() => setCreating(true)}
          >
            <Plus size={16} />
          </Button>
        </div>
      )}
    </div>
  );
}

function SavingBody({ progress, sel }: { progress: number; sel: number }) {
  const done = Math.round(progress * sel);
  return (
    <div className="flex flex-col gap-3 px-4 py-6">
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-200"
          style={{ width: `${Math.round(progress * 100)}%` }}
          data-testid="import-progress-bar"
        />
      </div>
      <div className="text-xs text-muted-foreground">
        {done} из {sel} · {progress >= 1 ? 'готово' : 'запись…'}
      </div>
    </div>
  );
}

function SegBtn({
  active,
  onClick,
  testId,
  children,
}: {
  active: boolean;
  onClick: () => void;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={
        'flex-1 px-2 py-2 text-xs font-semibold transition-colors ' +
        (active
          ? 'bg-primary text-primary-foreground'
          : 'bg-background text-muted-foreground hover:bg-accent')
      }
    >
      {children}
    </button>
  );
}

/**
 * Режим «Таблицей» (AK-369 фрейм 4б): компактная таблица №/имя/га/статус.
 * Tab перебрасывает вниз по колонке имён (нативно, инпуты в DOM-порядке).
 * Дубликаты внизу помечены строкой-статусом (в таблице группу не сворачиваем —
 * там важен быстрый Tab-проход по всему списку).
 */
function NameTable({
  rows,
  autoBase,
  onRename,
  onFocus,
}: {
  rows: readonly ImportRow[];
  autoBase: string;
  onRename: (index: number, name: string) => void;
  onFocus: (index: number | null) => void;
}) {
  return (
    <table className="w-full border-collapse text-xs" data-testid="import-name-table">
      <thead>
        <tr className="text-xs uppercase tracking-wide text-muted-foreground">
          <th className="w-8 px-2 py-2 text-left font-semibold">№</th>
          <th className="px-2 py-2 text-left font-semibold">Имя поля</th>
          <th className="w-14 px-2 py-2 text-right font-semibold">га</th>
          <th className="w-8 px-2 py-2 text-center font-semibold">Статус</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => {
          const dup = isDuplicate(r);
          const ph = autoNameForRow(autoBase, rows, r.index);
          return (
            <tr
              key={r.index}
              className="border-t border-border/60 hover:bg-accent/40"
              onFocus={() => onFocus(r.index)}
              data-testid={`import-name-row-${r.index}`}
            >
              <td className="px-2 py-1 text-muted-foreground tabular-nums">{i + 1}</td>
              <td className="px-1 py-1">
                {r.valid ? (
                  <input
                    data-testid={`import-name-input-${r.index}`}
                    value={r.name}
                    placeholder={ph}
                    onChange={(e) => onRename(r.index, e.target.value)}
                    className="w-full bg-transparent px-1 py-1 text-xs outline-none rounded border border-transparent focus:border-accent-brand"
                  />
                ) : (
                  <span className="px-1 text-muted-foreground truncate block">
                    {r.name || `Контур ${r.index}`}
                  </span>
                )}
              </td>
              <td className="px-2 py-1 text-right tabular-nums text-muted-foreground">
                {r.areaHa != null && r.areaHa > 0
                  ? r.areaHa.toFixed(r.areaHa >= 10 ? 1 : 2).replace('.', ',')
                  : '—'}
              </td>
              <td className="px-2 py-1 text-center">
                <span
                  className={
                    'inline-flex ' +
                    (!r.valid || dup ? 'text-accent-brand' : 'text-primary')
                  }
                  title={!r.valid || dup ? 'Требует внимания' : 'Готово к импорту'}
                >
                  {!r.valid || dup ? (
                    <TriangleAlert size={14} aria-label="Требует внимания" />
                  ) : (
                    <Check size={14} aria-label="Готово к импорту" />
                  )}
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function ImportRowItem({
  row,
  dimmed,
  isEditing,
  autoName,
  onFocus,
  onToggle,
  onRename,
  onEdit,
  onRemove,
}: {
  row: ImportRow;
  dimmed: boolean;
  isEditing: boolean;
  autoName: string | null;
  onFocus: () => void;
  onToggle: () => void;
  onRename: (name: string) => void;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const nameRef = useRef<HTMLInputElement>(null);
  const dup = isDuplicate(row);
  const top = topDuplicate(row);
  const displayName = autoName ?? row.name;
  const nameIsAuto = autoName !== null || !row.name;
  return (
    <div
      data-testid={`import-row-${row.index}`}
      data-selected={row.selected ? 'true' : undefined}
      data-duplicate={dup ? 'true' : undefined}
      onClick={onFocus}
      className={
        'group rounded-lg border px-3 py-2 transition-colors cursor-pointer ' +
        (isEditing
          ? 'border-accent-brand bg-accent-brand/15'
          : row.selected
            ? 'border-accent-brand/40 bg-accent-brand/8'
            : dup
              ? 'border-accent-brand/25 bg-accent-brand/[0.04]'
              : 'border-transparent hover:bg-accent/60') +
        (dimmed ? ' opacity-50' : '')
      }
    >
      <div className="flex items-center gap-3">
        {/* Чекбокс */}
        <button
          type="button"
          aria-label={row.selected ? 'Снять выбор' : 'Выбрать'}
          data-testid={`import-row-cb-${row.index}`}
          disabled={!row.valid}
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          className={
            'flex h-[18px] w-[18px] flex-none items-center justify-center rounded border ' +
            (!row.valid
              ? 'border-border bg-muted cursor-not-allowed'
              : row.selected
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-input bg-background')
          }
        >
          {row.selected && row.valid && <Check size={14} />}
        </button>

        {/* Имя + мета */}
        <div className="min-w-0 flex-1">
          {row.valid ? (
            <input
              ref={nameRef}
              data-testid={`import-row-name-${row.index}`}
              value={displayName}
              placeholder={autoName ?? 'без имени'}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => onRename(e.target.value)}
              className={
                'w-full bg-transparent text-sm font-semibold outline-none border-b border-transparent focus:border-accent-brand ' +
                (nameIsAuto ? 'text-muted-foreground italic' : 'text-foreground')
              }
            />
          ) : (
            <div className="text-sm font-semibold text-foreground truncate">
              {row.name || `Контур ${row.index}`}
            </div>
          )}
          {/* Мета-строка: дубликат / невалид / пересечение / площадь */}
          {!row.valid ? (
            <div className="flex items-center gap-1 text-xs text-destructive truncate">
              <TriangleAlert size={14} aria-hidden className="shrink-0" />
              {row.error ?? 'невалидный контур'} — исключён
            </div>
          ) : dup && top ? (
            <div
              data-testid={`import-row-dupbadge-${row.index}`}
              className="flex items-center gap-1 text-xs text-accent-brand truncate font-medium"
            >
              <TriangleAlert size={14} aria-hidden className="shrink-0" />
              дубликат: {top.name} ({top.overlap_pct}%)
            </div>
          ) : row.intersectsWith ? (
            <div className="flex items-center gap-1 text-xs text-accent-brand truncate">
              <TriangleAlert size={14} aria-hidden className="shrink-0" />
              пересекается с «{row.intersectsWith}»
            </div>
          ) : (
            <div className="text-xs text-muted-foreground tabular-nums">
              {formatAreaHa(row.areaHa)}
              {row.error ? ` · ${row.error}` : ''}
            </div>
          )}
        </div>

        {/* Статус */}
        <span
          className={
            'flex-none text-sm ' +
            (!row.valid ? 'text-destructive' : dup || row.intersectsWith ? 'text-accent-brand' : 'text-primary')
          }
        >
          {!row.valid || dup || row.intersectsWith ? (
            <TriangleAlert size={16} aria-label="Требует внимания" />
          ) : (
            <Check size={16} aria-label="Готово к импорту" />
          )}
        </span>
      </div>

      {/* Действия строки: уточнить / удалить (только для valid) */}
      {row.valid && (
        <div className="mt-2 flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
            data-testid={`import-row-edit-${row.index}`}
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
          >
            <Pencil size={14} />
            Уточнить контур
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive"
            data-testid={`import-row-remove-${row.index}`}
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
          >
            <Trash2 size={14} />
            Удалить
          </Button>
        </div>
      )}
    </div>
  );
}

function errorTitle(kind: string): string {
  switch (kind) {
    case 'too-large':
      return 'Файл слишком большой';
    case 'forbidden':
      return 'Нет прав на импорт';
    case 'unauthorized':
      return 'Сессия истекла';
    case 'empty':
      return 'В файле нет пригодных контуров';
    case 'network':
      return 'Не удалось загрузить файл';
    default:
      return 'Не смогли прочитать файл';
  }
}

/** Простой русский плюрал (1 поле / 2 поля / 5 полей). */
function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}

/**
 * Автоген-имя строки при nameMode='auto': «Поле N», где N берётся от базы
 * (следующее свободное) + порядковый номер valid-строки. Стабильно по позиции.
 */
function autoNameForRow(base: string, rows: readonly ImportRow[], index: number): string {
  const m = /(\d+)\s*$/.exec(base);
  const start = m ? Number.parseInt(m[1], 10) : 1;
  const prefix = base.replace(/\d+\s*$/, '').trim() || 'Поле';
  const validRows = rows.filter((r) => r.valid);
  const pos = validRows.findIndex((r) => r.index === index);
  return `${prefix} ${start + (pos < 0 ? 0 : pos)}`;
}
